import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

/** Types mirror backend/src/modules/hub (Wave C2). */

export type HubMode = 'live' | 'mock' | 'off';
export type HubAuthMode = 'user' | 'service';

export type HubFailureReason =
  | 'not_configured'
  | 'disabled'
  | 'no_user_token'
  | 'dev_token'
  | 'dns'
  | 'refused'
  | 'timeout'
  | 'tls'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'server'
  | 'bad_response'
  | 'network';

export interface HubConfigProblem {
  key: string;
  message: string;
}

export interface HubStatus {
  mode: HubMode;
  /** The transport ACTUALLY in use — a stored key wins over the HUB_AUTH env. */
  auth: HubAuthMode;
  connected: boolean;
  endpoint: string;
  baseUrl: string | null;
  hasServiceKey: boolean;
  /** Which rung of the ladder the key came from. */
  keySource: HubKeySource;
  /** What the hub is actually called with. One derivation, shared with /hub/connection. */
  transport: HubTransport;
  transportLabel: string;
  problems: HubConfigProblem[];
  organization: { authOrgId: string; name: string | null; slug: string | null };
  counts: { agreements: number; linked: number; unlinked: number; activeUnlinked: number };
  /** Org policy: every agreement must be linked to a hub project. */
  requireHubLink: boolean;
  ownership: { hub: string[]; financials: string[] };
}

export interface HubTestResult {
  ok: boolean;
  mode: string;
  endpoint: string;
  checkedAt: string;
  reason?: HubFailureReason;
  detail?: string;
  consumer?: string | null;
  latencyMs?: number;
}

// ---------------------------------------------------------------------------
// The hub SERVICE KEY (2026-08-14) — Settings → Hub → Solid Hub.
//
// Types mirror `backend/src/modules/hub/hub-connection.service.ts`. The same
// four routes, the same shape and the same words as the sales connection above:
// paste a key, prove it, say which one is in use, remove it.
// ---------------------------------------------------------------------------

export type HubKeySource = 'database' | 'environment' | 'none';
export type HubTransport = 'service' | 'user' | 'fixtures' | 'off';

export interface HubConnection {
  provider: 'HUB';
  source: HubKeySource;
  /** The sentence the screen prints — WHICH key is in use, in the server's words. */
  sourceLabel: string;
  transport: HubTransport;
  transportLabel: string;
  stored: boolean;
  usable: boolean;
  status: string | null;
  endpoint: string;
  baseUrl: string | null;
  /** Prefix and last 4 only. The key itself never leaves the server. */
  key: { masked: string | null; prefix: string | null; last4: string | null };
  connectedAs: { consumer: string | null; keySource: string | null; scopes: string[] } | null;
  connectedAt: string | null;
  connectedBy: string | null;
  rotatedAt: string | null;
  rotatedBy: string | null;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestReason: string | null;
  problems: HubConfigProblem[];
  reason: string | null;
  /** What removing the stored key would leave behind — said BEFORE it is pressed. */
  fallsBackTo: string;
  canManage: boolean;
  history: Array<{
    id: string;
    action: string;
    summary: string;
    detail: string | null;
    ok: boolean;
    actorName: string | null;
    actorEmail: string | null;
    at: string;
  }>;
}

export interface HubConnectionTestResult {
  ok: boolean;
  source: HubKeySource;
  sourceLabel: string;
  endpoint: string;
  checkedAt: string;
  latencyMs: number | null;
  consumer: string | null;
  keySource: string | null;
  scopes: string[];
  reason: string | null;
  detail: string | null;
}

export type MatchedBy = 'hubProjectId' | 'harvestId' | 'name';
export type LinkState = 'linked' | 'importable' | 'archived';

export interface HubProjectRow {
  hubProjectId: string;
  name: string;
  description: string | null;
  status: string | null;
  isPrivate: boolean;
  lastActivityAt: string | null;
  counts: { members: number | null; taskLists: number | null; messages: number | null };
  archivedInHub: boolean;
  archivedAt: string | null;
  clientOrgId: string;
  clientOrgName: string | null;
  clientIsOwnerOrg: boolean;
  client: { id: string; name: string; matchedBy: 'hubOrgId' | 'name' } | null;
  willCreateClient: boolean;
  linked: boolean;
  /** EVERY linked agreement — rollover chains and cross-funding alike. */
  agreements: Array<{ id: string; name: string }>;
  importable: boolean;
  suggestedMatch: { agreementId: string; agreementName: string; matchedBy: MatchedBy } | null;
  linkState: LinkState;
}

export interface HubProjectsResponse {
  connected: boolean;
  mode: string;
  endpoint: string;
  reason?: HubFailureReason;
  detail?: string;
  projects: HubProjectRow[];
}

export interface ImportDefaults {
  type: 'PROJECT' | 'SUPPORT' | 'RECURRING' | 'INTERNAL';
  billBy: 'HOURS' | 'FIXED' | 'NONE';
  hourlyRate?: number | null;
  budgetHours?: number | null;
  budgetAmount?: number | null;
}

export interface ImportResultRow {
  hubProjectId: string;
  name?: string;
  outcome: 'created' | 'linked' | 'already_linked' | 'skipped_archived' | 'not_found';
  reason?: string;
  agreementId?: string;
  agreementName?: string;
  clientName?: string;
  clientCreated?: boolean;
  matchedBy?: MatchedBy;
  updatedFields?: string[];
}

export interface ImportResponse {
  connected: boolean;
  mode: string;
  endpoint?: string;
  reason?: HubFailureReason;
  detail?: string;
  counts: { created: number; linked: number; alreadyLinked: number; skipped: number; clientsCreated: number };
  results: ImportResultRow[];
}

export interface SyncResponse {
  connected: boolean;
  mode: string;
  endpoint?: string;
  reason?: HubFailureReason;
  detail?: string;
  startedAt: string;
  finishedAt: string;
  counts: { linked: number; updated: number; unchanged: number; archived: number; missingInHub: number };
  changes: Array<{
    agreementId: string;
    agreementName: string;
    hubProjectId: string;
    outcome: 'updated' | 'missing_in_hub';
    fields?: string[];
    newName?: string | null;
  }>;
}

const KEY = ['hub'] as const;

export function useHubStatus() {
  return useQuery({
    queryKey: [...KEY, 'status'],
    queryFn: () => api.get<HubStatus>('/hub/status'),
  });
}

export function useHubProjects(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, 'agreements'],
    queryFn: () => api.get<HubProjectsResponse>('/hub/projects'),
    // Callers that only need a display NAME (the client hub badge) skip the
    // request entirely when there is no link to name — a hub call must never
    // be the price of rendering a page.
    enabled: options.enabled ?? true,
  });
}

export function useTestHubConnection() {
  return useMutation({ mutationFn: () => api.post<HubTestResult>('/hub/test-connection') });
}

const HUB_CONNECTION_KEY = ['hub', 'connection'] as const;

export function useHubConnection() {
  return useQuery({
    queryKey: HUB_CONNECTION_KEY,
    queryFn: () => api.get<HubConnection>('/hub/connection'),
  });
}

/** Changing the key changes the transport every hub read uses — refresh them all. */
function useHubConnectionInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: KEY });
  };
}

export function useSaveHubKey() {
  const invalidate = useHubConnectionInvalidate();
  return useMutation({
    mutationFn: (body: { apiKey: string; baseUrl?: string }) =>
      api.post<HubConnection>('/hub/connection', body),
    onSuccess: invalidate,
  });
}

export function useRemoveHubKey() {
  const invalidate = useHubConnectionInvalidate();
  return useMutation({
    mutationFn: () => api.delete<{ removed: boolean; fallsBackTo: string }>('/hub/connection'),
    onSuccess: invalidate,
  });
}

export function useTestHubKey() {
  const invalidate = useHubConnectionInvalidate();
  return useMutation({
    mutationFn: () => api.post<HubConnectionTestResult>('/hub/connection/test'),
    onSuccess: invalidate,
  });
}

/** Any mutation can change our agreements/clients — refresh the screens that read them. */
function useHubInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: KEY });
    void queryClient.invalidateQueries({ queryKey: ['agreements'] });
    void queryClient.invalidateQueries({ queryKey: ['clients'] });
  };
}

export function useImportHubProjects() {
  const invalidate = useHubInvalidate();
  return useMutation({
    mutationFn: (body: { hubProjectIds: string[]; defaults: ImportDefaults }) =>
      api.post<ImportResponse>('/hub/projects/import', body),
    onSuccess: invalidate,
  });
}

export function useLinkHubProject() {
  const invalidate = useHubInvalidate();
  return useMutation({
    mutationFn: ({ hubProjectId, agreementId }: { hubProjectId: string; agreementId: string }) =>
      api.post(`/hub/projects/${encodeURIComponent(hubProjectId)}/link`, { agreementId }),
    onSuccess: invalidate,
  });
}

/** Bulk: link EVERY selected agreement to one hub project, one call. */
export function useLinkHubProjectBulk() {
  const invalidate = useHubInvalidate();
  return useMutation({
    mutationFn: ({ hubProjectId, agreementIds }: { hubProjectId: string; agreementIds: string[] }) =>
      api.post<{ linked: number; agreementNames: string[] }>(
        `/hub/projects/${encodeURIComponent(hubProjectId)}/link`,
        { agreementIds },
      ),
    onSuccess: invalidate,
  });
}

export function useUnlinkHubProject() {
  const invalidate = useHubInvalidate();
  return useMutation({
    mutationFn: ({ hubProjectId, agreementId }: { hubProjectId: string; agreementId?: string }) =>
      api.delete(
        `/hub/projects/${encodeURIComponent(hubProjectId)}/link` +
          (agreementId ? `?agreementId=${encodeURIComponent(agreementId)}` : ''),
      ),
    onSuccess: invalidate,
  });
}

export function useHubSync() {
  const invalidate = useHubInvalidate();
  return useMutation({ mutationFn: () => api.post<SyncResponse>('/hub/sync'), onSuccess: invalidate });
}

/** Human explanation for each precise failure reason. */
export const HUB_FAILURE_EXPLANATIONS: Record<HubFailureReason, string> = {
  not_configured: 'The hub connection is not configured — set HUB_API_URL (and HUB_MODE=live).',
  disabled: 'HUB_MODE=off — hub linking is turned off for this deployment.',
  no_user_token: 'This request carried no access token to forward to the hub. Sign in again.',
  dev_token:
    'You are signed in with a dev token ("dev:<email>"), which the hub cannot verify. Use HUB_MODE=mock locally, or sign in through the real identity provider.',
  dns: 'DNS lookup failed — the host in HUB_API_URL does not resolve.',
  refused: 'Connection refused — nothing is listening on that host/port (is hub-api running?).',
  timeout: 'Timed out waiting for a response — the hub is slow, or unreachable behind a firewall.',
  tls: 'TLS certificate was rejected — check the certificate chain, or http vs https.',
  unauthorized: 'The hub rejected our credentials (401) — the token expired, or the service key is wrong.',
  forbidden: 'The hub refused this resource (403) — this user may not view it.',
  not_found: 'The hub returned 404 — wrong path, or the project no longer exists there.',
  server: 'The hub returned a 5xx — it is up but failing.',
  bad_response: 'The response was not the JSON we expect — HUB_API_URL may point at the web app, not the API.',
  network: 'Network error before a response arrived.',
};

/** "3 minutes ago" — small enough not to warrant a dependency. */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const deltaSeconds = Math.round((then - Date.now()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ];
  let value = deltaSeconds;
  for (const [unit, size] of units) {
    if (Math.abs(value) < size) {
      return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(Math.round(value), unit);
    }
    value /= size;
  }
  return 'never';
}

// ---------------------------------------------------------------------------
// Sales connection (Wave D0b) — the Hub tab is the home for ecosystem
// connections, so the sales API key lives beside hub project linking.
// Types mirror `backend/src/modules/sales/sales-connection.service.ts`.
// ---------------------------------------------------------------------------

export type SalesConnectionSource = 'database' | 'environment' | 'fixtures' | 'none';

export interface SalesConnection {
  source: SalesConnectionSource;
  /** The sentence the screen prints — WHICH key is in use, in words. */
  sourceLabel: string;
  usable: boolean;
  live: boolean;
  endpoint: string;
  /** First 8 characters only. The key itself never leaves the server. */
  keyPrefix: string | null;
  connectedAs: string | null;
  connectedAt: string | null;
  lastTestedAt: string | null;
  status: string | null;
  supportsPaymentSchedules: boolean | null;
  problems: Array<{ key: string; message: string }>;
  reason: string | null;
}

export interface SalesTestResult {
  ok: boolean;
  source: SalesConnectionSource;
  sourceLabel: string;
  endpoint: string;
  checkedAt: string;
  identity: string | null;
  keyCount: number | null;
  dealCount: number | null;
  openDealCount: number | null;
  supportsPaymentSchedules: boolean | null;
  reason: string | null;
  detail: string | null;
  latencyMs: number | null;
}

const SALES_KEY = ['sales', 'connection'] as const;

export function useSalesConnection() {
  return useQuery({
    queryKey: SALES_KEY,
    queryFn: () => api.get<SalesConnection>('/sales/connection'),
  });
}

/** Changing the key changes what Projected shows — refresh both. */
function useSalesInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: SALES_KEY });
    void queryClient.invalidateQueries({ queryKey: ['sales', 'projected'] });
    void queryClient.invalidateQueries({ queryKey: ['cashflow'] });
  };
}

export function useConnectSales() {
  const invalidate = useSalesInvalidate();
  return useMutation({
    mutationFn: (body: { apiKey: string; baseUrl?: string }) =>
      api.post<SalesConnection>('/sales/connection', body),
    onSuccess: invalidate,
  });
}

export function useDisconnectSales() {
  const invalidate = useSalesInvalidate();
  return useMutation({
    mutationFn: () => api.delete('/sales/connection'),
    onSuccess: invalidate,
  });
}

export function useTestSalesConnection() {
  const invalidate = useSalesInvalidate();
  return useMutation({
    mutationFn: () => api.post<SalesTestResult>('/sales/connection/test'),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// The opportunity threshold — *"an opportunity percentage line in the sand"*.
// Org-level (`Organization.settings`), read and written here because it governs
// what comes out of the sales connection it sits beside.
// ---------------------------------------------------------------------------

const THRESHOLD_KEY = ['sales', 'threshold'] as const;

export function useOpportunityThreshold() {
  return useQuery({
    queryKey: THRESHOLD_KEY,
    queryFn: () => api.get<{ threshold: number }>('/sales/threshold'),
  });
}

/**
 * Moving the line changes which deals the Projected checklist offers — and, for
 * the ones it drops, the Potential row of the cash grid. It can never change
 * money already ticked, but it must still refresh both screens, or they read a
 * stale answer for as long as the report cache lives.
 */
export function useSetOpportunityThreshold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threshold: number) =>
      api.put<{ threshold: number }>('/sales/threshold', { threshold }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THRESHOLD_KEY });
      void queryClient.invalidateQueries({ queryKey: ['sales', 'projected'] });
      void queryClient.invalidateQueries({ queryKey: ['cashflow'] });
    },
  });
}
